<template>
  <div class="footer-wrapper">
    <!-- 在线统计信息 -->
    <div v-if="onlineStatsConfig?.showOnlineStats !== false" class="online-stats">
      <span class="live-dot" aria-hidden="true"></span>
      <span class="stat-item">
        <span class="stat-label">当前在线用户</span>
        <span class="stat-number">{{ displayUsers.toLocaleString() }}</span>
      </span>
      <!-- <span class="stat-divider">|</span> -->
      <span class="stat-item">
        <span class="stat-label">当前在线企业</span>
        <span class="stat-number">{{ displayEnterprises.toLocaleString() }}</span>
      </span>
    </div>
    <!-- 底部版权信息 -->
    <div class="footerInfoBar">{{ $t("sys.reg.s7") }} | <a href="https://beian.miit.gov.cn" target="_blank">浙 ICP 备 2024065161 号 -3</a>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import onlineStatsConfig from '@/config/online-stats';

// 峰值配置：由 config 集中管理，表示用户 / 企业在线人数在一天中的峰值大小
// 做数值兑底，避免配置缺失 / 非法时出现 NaN 或 0
const toPositive = (val, fallback) => (Number(val) > 0 ? Number(val) : fallback);
const PEAK_USERS = toPositive(onlineStatsConfig?.peakUsers, 3500);
const PEAK_ENTERPRISES = toPositive(onlineStatsConfig?.peakEnterprises, 1000);

// 目标值（由挂钟时间确定性计算，刷新页面结果一致）
const onlineUsers = ref(PEAK_USERS);
const onlineEnterprises = ref(PEAK_ENTERPRISES);

// 页面展示值（平滑动画过渡到目标值）
const displayUsers = ref(PEAK_USERS);
const displayEnterprises = ref(PEAK_ENTERPRISES);

let driftIntervalId = null;
let rafId = null;

const TWO_PI = Math.PI * 2;

// 基于挂钟时间的确定性多正弦叠加波动，返回值范围约 [-1, 1]
// 关键：同一时刻刷新得到相同结果，随时间平滑变化，不再依赖 Math.random
const wave = (seconds, periods) =>
  periods.reduce((sum, p) => sum + Math.sin((seconds / p.t) * TWO_PI + p.phase) * p.amp, 0);

// 各时段的“负载区间”（占峰值百分比），参考运营数据表格：
// 00:00-02:00 20%~35% | 02:00-07:00 10%~20% | 07:00-09:00 25%~50%
// 09:00-18:00 80%~100% | 18:00-24:00 45%~75%
const LOAD_BLOCKS = [
  { start: 0, end: 2, lo: 0.20, hi: 0.35 },   // 国内低谷，欧美仍有一定在线
  { start: 2, end: 7, lo: 0.10, hi: 0.20 },   // 国内低谷，欧美部分用户在线
  { start: 7, end: 9, lo: 0.25, hi: 0.50 },   // 国内陆续上线，欧美仍在线
  { start: 9, end: 18, lo: 0.80, hi: 1.00 },  // 全天核心高负载时段
  { start: 18, end: 24, lo: 0.45, hi: 0.75 }, // 国内下班，欧美进入主要工作时间
];

// 相邻时段边界处的平滑过渡时长（小时），避免跨时段负载突跳
const TRANSITION_H = 0.5;

// 获取当前时刻的负载区间 { lo, hi }（占峰值百分比），边界处与相邻时段线性过渡
const getLoadBandAt = (hour, minute, second = 0) => {
  const t = hour + minute / 60 + second / 3600;
  const n = LOAD_BLOCKS.length;
  let idx = LOAD_BLOCKS.findIndex((b) => t >= b.start && t < b.end);
  if (idx === -1) idx = n - 1;
  const cur = LOAD_BLOCKS[idx];

  const toStart = t - cur.start;
  const toEnd = cur.end - t;

  // 默认使用当前时段的区间
  let lo = cur.lo;
  let hi = cur.hi;

  if (toStart < TRANSITION_H && toStart <= toEnd) {
    // 靠近起始边界（且更靠近起始侧）：与上一时段平滑过渡（边界处取两段中点）
    const prev = LOAD_BLOCKS[(idx - 1 + n) % n];
    const w = 0.5 + toStart / (2 * TRANSITION_H);
    lo = prev.lo + (cur.lo - prev.lo) * w;
    hi = prev.hi + (cur.hi - prev.hi) * w;
  } else if (toEnd < TRANSITION_H) {
    // 靠近结束边界：与下一时段平滑过渡
    const next = LOAD_BLOCKS[(idx + 1) % n];
    const w = 0.5 - toEnd / (2 * TRANSITION_H);
    lo = cur.lo + (next.lo - cur.lo) * w;
    hi = cur.hi + (next.hi - cur.hi) * w;
  }
  return { lo, hi };
};

// 多正弦叠加的有界振荡，振幅之和为 1，返回值稳定落在 [0, 1]
// 用于让负载在 [lo, hi] 区间内平滑往复，且同一时刻刷新结果一致
const oscillate01 = (seconds, periods) => (wave(seconds, periods) + 1) / 2;

// 以北京时间（UTC+8）为基准取得作息时刻，
// 保证不同时区的访客看到一致的“国内/欧美”作息曲线，而非跟随各自本地时间
const getBeijingTime = () => {
  const bj = new Date(Date.now() + 8 * 3600 * 1000);
  return { hour: bj.getUTCHours(), minute: bj.getUTCMinutes(), second: bj.getUTCSeconds() };
};

// 计算当前时刻的目标值（完全由时间决定，不含随机数）
const getTargetStats = () => {
  const { hour, minute, second } = getBeijingTime();
  const { lo, hi } = getLoadBandAt(hour, minute, second);
  const seconds = Date.now() / 1000;

  // 用户负载：在当前时段区间 [lo, hi] 内缓慢往复（主周期数十分钟，贴近真实变化节奏）
  const userLoad = lo + (hi - lo) * oscillate01(seconds, [
    { t: 1500, phase: 0, amp: 0.5 },
    { t: 3600, phase: 1.7, amp: 0.3 },
    { t: 9000, phase: 0.6, amp: 0.2 },
  ]);
  const userTarget = PEAK_USERS * userLoad;

  // 企业负载：与用户共用同一区间，但使用不同周期/相位，看起来各自独立
  const entLoad = lo + (hi - lo) * oscillate01(seconds, [
    { t: 1300, phase: 2.5, amp: 0.5 },
    { t: 4200, phase: 0.4, amp: 0.3 },
    { t: 7800, phase: 1.1, amp: 0.2 },
  ]);
  const enterpriseTarget = PEAK_ENTERPRISES * entLoad;

  return { userTarget, enterpriseTarget };
};

// 刷新目标值（完全由时间决定，刷新页面同一时刻结果一致）
const updateTargets = () => {
  const { userTarget, enterpriseTarget } = getTargetStats();
  onlineUsers.value = Math.max(1, Math.round(userTarget));
  onlineEnterprises.value = Math.max(1, Math.round(enterpriseTarget));
};

// 数字滚动动画：将展示值平滑靠近真实值
const animateDisplay = () => {
  const stepToward = (current, target) => {
    const diff = target - current;
    if (Math.abs(diff) < 1) return target;
    // 每帧前进差值的 6%，至少移动 1，形成平缓的滚动计数观感
    return current + Math.sign(diff) * Math.max(1, Math.abs(diff) * 0.06);
  };

  displayUsers.value = Math.round(stepToward(displayUsers.value, onlineUsers.value));
  displayEnterprises.value = Math.round(stepToward(displayEnterprises.value, onlineEnterprises.value));

  rafId = requestAnimationFrame(animateDisplay);
};

onMounted(() => {
  // 初始化：直接定位到当前时刻的目标值（确定性），刷新前后保持一致
  updateTargets();
  displayUsers.value = onlineUsers.value;
  displayEnterprises.value = onlineEnterprises.value;

  // 每 5 秒按时间重算目标值，展示值再平滑过渡
  driftIntervalId = setInterval(updateTargets, 5000);

  // 启动数字滚动动画
  rafId = requestAnimationFrame(animateDisplay);
});

onUnmounted(() => {
  if (driftIntervalId) {
    clearInterval(driftIntervalId);
  }
  if (rafId) {
    cancelAnimationFrame(rafId);
  }
});
</script>

<style lang="less" scoped>
.footer-wrapper {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 6%;
  width: 100%;
  padding: 0 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

// 在线统计信息
.online-stats {
  font-size: 14px;
  color: #666;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  padding: 8px 20px;
  max-width: 100%;
  justify-content: center;

  // 实时在线指示灯
  .live-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #52c41a;
    box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.6);
    animation: live-pulse 1.8s ease-out infinite;
    flex-shrink: 0;
  }

  .stat-item {
    color: #666;
    font-weight: normal;
    white-space: nowrap;
    display: inline-flex;
    align-items: baseline;
  }

  .stat-label {
    &::after {
      content: '：';
      margin-right: 2px;
    }
  }

  .stat-number {
    color: #1890ff;
    font-weight: 600;
    font-size: 15px;
    display: inline-block;
    min-width: 48px;
    //text-align: right;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.5px;
  }
  
  .stat-divider {
    color: #d9d9d9;
  }
}

@keyframes live-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.55);
    opacity: 1;
  }
  70% {
    box-shadow: 0 0 0 6px rgba(82, 196, 26, 0);
    opacity: 0.85;
  }
  100% {
    box-shadow: 0 0 0 0 rgba(82, 196, 26, 0);
    opacity: 1;
  }
}

// 底部版权信息等
.footerInfoBar {
  font-size: 14px;
  color: #666;
}
</style>